<mulo-page-base
    [loading]="loading()"
    backToTopLabel="{{ 'research.portal.fulldisplay.backToTopButton' | translate }}"
    [rtl]="i18nService.isLangRtl()"
>
    @if (!record()) {
        <mulo-page-mast [title]="emptyRecordTitle">
            <mulo-infobar color="warn" icon="alert-outline" corners="rounded">
                {{ 'research.portal.fulldisplay.errorStatus.' + errorStatus | translate }}
            </mulo-infobar>
        </mulo-page-mast>
    }
    <mulo-nav-main-layout
        slot="main-page-content"
        [navPosition]="media.is('lt-lg') ? 'top' : 'left'"
        navBehavior="default"
        [stickyOffset]="media.is('lt-lg') ? 0 : 30"
        [stickyZIndex]="10"
        [navSize]="media.is('lt-lg') ? 48 : 150"
        [navShifted]="media.is('lt-lg') ? null : 'out'"
    >
        @if (_window?.history?.length > 1) {
            <a
                slot="nav"
                (click)="goBack()"
                (muloMultipleEvents)="goBack()"
                mat-button
                class="has-icon back-button"
                [class.margin-top-sm]="showEditBtn()"
                [disabled]="_window?.history?.length < 2"
            >
                <mat-icon class="mat-icon-rtl-mirror" svgIcon="chevron-left"></mat-icon>
                {{ 'research.general.back' | translate }}
            </a>
        }

        @if (record()) {
            <ng-container slot="main">
                @if (showEditBtn()) {
                    <div class="edit-mode-controls">
                        <a
                            [disabled]="!isEditable()"
                            (click)="toggleDisplayMode()"
                            (muloMultipleEvents)="toggleDisplayMode()"
                            class="has-icon margin-right-sm"
                            mat-raised-button
                        >
                            <mat-icon [svgIcon]="displayMode() !== 'edit' ? 'pencil' : 'eye-outline'"></mat-icon>
                            {{
                                'research.portal.fulldisplay.displayModeBtn.' +
                                    (displayMode() !== 'edit' ? 'edit' : 'view') | translate
                            }}</a
                        >
                        <span class="spacer"></span>
                        @if (displayMode() === 'edit') {
                            <div class="submit-changes-wrap">
                                <a
                                    class="has-icon has-icon-after"
                                    mat-raised-button
                                    (click)="onSubmitBtnClick(submitDialog)"
                                    [disabled]="submitLoading() || (assetRevision() | keyvalue)?.length === 0"
                                    color="primary"
                                >
                                    @if (submitLoading()) {
                                        <mat-spinner
                                            @widthInAnimation
                                            @widthOutAnimation
                                            class="margin-right-sm"
                                            diameter="20"
                                        ></mat-spinner>
                                    }
                                    <span>
                                        {{ 'research.portal.fulldisplay.submitChanges.btn' | translate }}
                                    </span>

                                    <mat-icon class="mat-icon-rtl-mirror" svgIcon="send"></mat-icon>
                                </a>
                                <ng-template #submitDialog>
                                    <h2 mat-dialog-title>
                                        <span>{{
                                            'research.portal.fulldisplay.submitChanges.dialog.title' | translate
                                        }}</span>
                                        <button mat-icon-button class="dialog-close-button" mat-dialog-close>
                                            <mat-icon svgIcon="close"></mat-icon>
                                        </button>
                                    </h2>

                                    <mat-dialog-content>
                                        <p class="margin-top-md margin-bottom-sm semibold">
                                            {{
                                                'research.portal.fulldisplay.submitChanges.dialog.question' | translate
                                            }}
                                        </p>
                                        <p class="margin-bottom-md">
                                            {{ 'research.portal.fulldisplay.submitChanges.dialog.info' | translate }}
                                        </p>
                                        <mat-form-field
                                            class="is-comfortably-readable"
                                            [muloInputExpander]="submitNote()"
                                        >
                                            <mat-label>{{
                                                'research.portal.fulldisplay.submitChanges.dialog.noteInput.label'
                                                    | translate
                                            }}</mat-label>
                                            <input matInput [(ngModel)]="submitNote" />
                                        </mat-form-field>

                                        @if (requiredFieldsMissing().length > 0) {
                                            <p class="margin-top-sm margin-bottom-sm semibold palette-warn-color">
                                                {{ 'research.general.warning.summary.default.default' | translate }}
                                            </p>

                                            <p class="palette-warn-color semibold">
                                                {{ 'research.deposit.required' | translate }}:
                                            </p>
                                            <ul class="missing-field-list">
                                                @for (field of requiredFieldsMissing(); track field) {
                                                    @let label = requiredFieldLabels().get(field);
                                                    <li>{{ isLocalField(field) ? (label | translate) : label }}</li>
                                                }
                                            </ul>
                                        }
                                    </mat-dialog-content>
                                    <mat-dialog-actions align="end" class="padding-sm">
                                        <button mat-button mat-dialog-close>
                                            {{ 'research.aria.dialog.cancel' | translate }}
                                        </button>
                                        <button
                                            mat-raised-button
                                            color="primary"
                                            [mat-dialog-close]="true"
                                            [disabled]="requiredFieldsMissing().length > 0"
                                        >
                                            {{ 'research.portal.fulldisplay.submitChanges.dialog.confirm' | translate }}
                                        </button>
                                    </mat-dialog-actions>
                                </ng-template>
                            </div>
                            <em class="padding-top-sm padding-bottom-sm">{{
                                'research.portal.fulldisplay.submitChanges.helpText' | translate
                            }}</em>
                        }
                    </div>
                }

                @if (displayMode() === 'edit') {
                    <mulo-context-frame
                        class="mast-form"
                        (close)="isMastFormOpen.set(false)"
                        [isOpen]="isMastFormOpen()"
                        [showOpenBtn]="false"
                    >
                        <form
                            (ngSubmit)="handleSectionSubmit(mastForm, isMastFormOpen)"
                            [formGroup]="mastForm"
                            class="padding-right-sm"
                        >
                            <esp-deposit-type
                                [selectedTypeCtrl]="mastForm.get(AssetFieldTypeEnum.AssetType)"
                                formFieldClass="is-comfortably-readable"
                            ></esp-deposit-type>
                            <div>
                                <mat-form-field
                                    class="is-comfortably-readable"
                                    [muloInputExpander]="mastForm.get(AssetFieldTypeEnum.Title).value"
                                >
                                    <mat-label>{{
                                        'esploro.research.conf.research.portal.label.prefix.esploroAssetModel.titles.mainTitle'
                                            | translate
                                    }}</mat-label>
                                    <input
                                        autocomplete="off"
                                        [formControlName]="AssetFieldTypeEnum.Title"
                                        matInput
                                        type="text"
                                    />
                                    <mat-error
                                        [innerHtml]="'research.settings.field.required.error.message' | translate"
                                    ></mat-error>
                                </mat-form-field>
                                <mat-form-field
                                    class="is-comfortably-readable"
                                    [muloInputExpander]="mastForm.get(AssetFieldTypeEnum.Subtitle).value"
                                >
                                    <mat-label>{{
                                        'esploro.research.conf.research.portal.label.prefix.esploroAssetModel.titles.mainTitleSubTitle'
                                            | translate
                                    }}</mat-label>
                                    <input
                                        autocomplete="off"
                                        [formControlName]="AssetFieldTypeEnum.Subtitle"
                                        matInput
                                        type="text"
                                    />
                                    <mat-error
                                        [innerHtml]="'research.settings.field.required.error.message' | translate"
                                    ></mat-error>
                                </mat-form-field>
                            </div>
                            <esp-deposit-doi
                                [assetType]="mastForm.get(AssetFieldTypeEnum.AssetType).value"
                                [doiControl]="mastForm.get(AssetFieldTypeEnum.Doi)"
                                [isReservable]="false"
                                placeholder="research.status.doi"
                            ></esp-deposit-doi>

                            <div class="save-btn-wrap">
                                <button
                                    [disabled]="mastForm.invalid || sectionSubmitInProgress()"
                                    class="is-tight margin-left-sm margin-bottom-sm"
                                    mat-raised-button
                                    type="submit"
                                >
                                    {{ 'research.portal.entity.item.save' | translate }}
                                </button>
                            </div>
                        </form>
                    </mulo-context-frame>
                }
                @if (!isMastFormOpen()) {
                    <mulo-page-mast
                        [ngClass]="{ 'editable mulo-elevated-list-item': displayMode() === 'edit' && mastForm.enabled }"
                        [class.margin-top-sm]="showEditBtn()"
                        [title]="assetTitleDisplay()"
                        [useHtmlAsTitle]="true"
                        [thumbnailUrl]="fullAssetPageRep()?.thumbnailUrl"
                    >
                        <div slot="pre-title">
                            <span class="small-caps semibold margin-right-xs"
                                >{{
                                    'portal.' +
                                        (assetRevision()?.[AssetFieldTypeEnum.AssetType] ??
                                            fullAssetPageRep()?.resourceTypeValue) | translate
                                }}
                                @if (fullAssetPageRep()?.displaySubTypeInTitle) {
                                    @if (fullAssetPageRep()?.entityModel.resourceSubType; as resourceSubType) {
                                        {{ ' - ' }}
                                        {{ resourceSubType | defaultTranslate: { default: resourceSubType } }}
                                    }
                                }
                            </span>
                            @if (isOpenAccess()) {
                                <mulo-separator
                                    orientation="vertical"
                                    length="14"
                                    class="margin-right-xs"
                                ></mulo-separator>
                                <mulo-status-tag flavor="plain" [icon]="useAltOAIcon() ? 'reading' : 'open-access'"
                                    >{{ 'research.results.openaccess' | translate }}
                                </mulo-status-tag>
                            }

                            @if (isPeerReviewed()) {
                                <mulo-separator
                                    orientation="vertical"
                                    length="14"
                                    class="margin-right-xs"
                                ></mulo-separator>
                                <mulo-status-tag flavor="plain" icon="peer-reviewed"
                                    >{{ 'research.results.peerreviewed' | translate }}
                                </mulo-status-tag>
                            }
                            @if (isEditable() === false) {
                                <mulo-status-tag
                                    [tooltip]="'research.portal.fulldisplay.lockedForRevisions.tooltip' | translate"
                                    >{{ 'research.portal.fulldisplay.lockedForRevisions' | translate }}
                                </mulo-status-tag>
                            }
                        </div>

                        <esp-authors-essentials
                            [creators]="creators()"
                            [creatorsConfig]="creatorsConfig()"
                            [showUserDetails]="showUserDetails()"
                        >
                        </esp-authors-essentials>

                        <esp-asset-page-metadata-essentials
                            [essentials]="essentials"
                            [fullAssetPageRep]="fullAssetPageRep()"
                            [assetsIdentifiersToDisplay]="assetsIdentifiersToDisplay"
                        >
                        </esp-asset-page-metadata-essentials>
                        <div class="action-btns" hideOnPrint>
                            @if (!isQuickAccess()) {
                                @if (displayDownloadButton()) {
                                    <button
                                        mat-raised-button
                                        color="primary"
                                        routerLink="."
                                        fragment="file-{{ fileViewBtnIdx() }}"
                                        queryParamsHandling="preserve"
                                        [replaceUrl]="true"
                                        (click)="handleStatistics()"
                                        class="margin-right-xs has-icon"
                                    >
                                        <mat-icon svgIcon="eye-outline"></mat-icon>
                                        {{ 'research.portal.fulldisplay.view.action' | translate }}
                                    </button>
                                }
                                @if (displayShareButton()) {
                                    <button
                                        mat-raised-button
                                        (click)="showShareDialog()"
                                        class="margin-right-xs has-icon"
                                    >
                                        <mat-icon svgIcon="share"></mat-icon>
                                        {{ 'research.portal.fulldisplay.share.action' | translate }}
                                    </button>
                                }
                                @if (displayExportButton()) {
                                    <button class="margin-right-xs" mat-raised-button (click)="showExportDialog()">
                                        {{ 'research.portal.fulldisplay.sendTo.export.action' | translate }}
                                    </button>
                                }
                            }
                        </div>

                        @if (collections?.length) {
                            <div class="margin-top-md">
                                <esp-collections-essentials [collections]="collections"></esp-collections-essentials>
                            </div>
                        }
                        @if (displayMode() === 'edit' && mastForm.enabled) {
                            <button
                                (click)="isMastFormOpen.set(true)"
                                [attr.aria-label]="'research.portal.fulldisplay.essentials.edit' | translate"
                                class="open-form-btn"
                                mat-button
                            >
                                <mat-icon svgIcon="pencil"></mat-icon>
                            </button>
                        }
                    </mulo-page-mast>
                }
            </ng-container>
        }
    </mulo-nav-main-layout>
    @for (section of sections(); track section.code) {
        @let sectionLabel = 'research.portal.fulldisplay.section.' + section.code | translate;
        @switch (section.code) {
            @case ('descriptionKeywords') {
                @if (
                    fullAssetPageRep() &&
                    (fullAssetPageRep()?.researchTopics.length > 0 ||
                        fullAssetPageRep()?.researchKeywords.length > 0 ||
                        fullAssetPageRep()?.description ||
                        (displayMode() === 'edit' && descriptionKeywordsForm.enabled))
                ) {
                    <mulo-page-section [title]="sectionLabel">
                        @if (displayMode() === 'edit' && descriptionKeywordsForm.enabled) {
                            <mulo-context-frame
                                (close)="isDescriptionKeywordsFormOpen.set(false)"
                                (open)="isDescriptionKeywordsFormOpen.set(true)"
                                [isOpen]="isDescriptionKeywordsFormOpen()"
                                btnIcon="pencil"
                                [btnLabel]="'research.aria.edit.button' | translate: { value: sectionLabel }"
                                [class.margin-bottom-xs]="!isDescriptionKeywordsFormOpen()"
                            >
                                <form
                                    (ngSubmit)="
                                        handleSectionSubmit(descriptionKeywordsForm, isDescriptionKeywordsFormOpen)
                                    "
                                    [formGroup]="descriptionKeywordsForm"
                                    class="padding-top-sm padding-right-sm"
                                >
                                    <div class="margin-bottom-xs">
                                        <esp-multi-language
                                            (languageChanged)="descKeywordLanguageChange($event)"
                                            (languageRemoved)="descKeywordRemoveLanguage($event)"
                                            [canAdd]="!!abstractControl().value || keywordsControl().value?.length"
                                            [languages]="descKeywordLanguageKeys()"
                                            [initKeys]="isDescriptionKeywordsFormOpen()"
                                            addLanguageLabel="description"
                                        />
                                    </div>

                                    <div>
                                        <mulo-rich-text-editor
                                            [editorContent]="abstractControl()"
                                            [placeholder]="'researcherDeposit.field.description' | translate"
                                            [sneakyToolbar]="true"
                                            [required]="fieldRequiredMap()?.get(AssetFieldTypeEnum.Abstract)"
                                            [requiredError]="'research.field.required.error.message' | customTranslate"
                                        />
                                    </div>
                                    <mat-form-field class="is-comfortably-readable keywords-field">
                                        <mat-label>{{ 'researcherDeposit.field.keywords' | translate }}</mat-label>
                                        <mat-chip-grid
                                            #chipGrid
                                            [formControl]="keywordsControl()"
                                            [required]="fieldRequiredMap()?.get(AssetFieldTypeEnum.Keywords)"
                                        >
                                            @for (keyword of keywordsControl().value; track keyword) {
                                                <mat-chip-row (removed)="removeKeyword(keyword)">
                                                    {{ keyword }}
                                                    <button
                                                        mat-icon-button
                                                        matChipRemove
                                                        [attr.aria-label]="
                                                            ('research.aria.deposit.keywords.remove' | translate) +
                                                            ' ' +
                                                            keyword
                                                        "
                                                    >
                                                        <mat-icon svgIcon="close" />
                                                    </button>
                                                </mat-chip-row>
                                            }
                                            <input
                                                [matChipInputFor]="chipGrid"
                                                [matChipInputSeparatorKeyCodes]="separatorKeysCodes"
                                                [matChipInputAddOnBlur]="true"
                                                (matChipInputTokenEnd)="addKeyword($event)"
                                                (paste)="handlePaste($event)"
                                            />
                                        </mat-chip-grid>
                                        <mat-error
                                            [innerHTML]="'research.field.required.error.message' | customTranslate"
                                        ></mat-error>
                                    </mat-form-field>

                                    <div class="save-btn-wrap">
                                        <button
                                            [disabled]="sectionSubmitInProgress()"
                                            class="is-tight margin-left-sm margin-bottom-sm"
                                            mat-raised-button
                                            type="submit"
                                        >
                                            {{ 'research.portal.entity.item.save' | translate }}
                                        </button>
                                    </div>
                                </form>
                            </mulo-context-frame>
                        }
                        @if (!isDescriptionKeywordsFormOpen()) {
                            <esp-description-and-keywords
                                [description]="abstractRevision() || fullAssetPageRep()?.description"
                                [researchTopics]="fullAssetPageRep()?.researchTopics"
                                [researchKeywords]="keywordsRevision() || fullAssetPageRep()?.researchKeywords"
                                [sectionOrder]="section.descriptionKeywords"
                            >
                            </esp-description-and-keywords>
                        }
                    </mulo-page-section>
                }
            }
            @case ('filesLinks') {
                @if (isFilesAndLinksSectionDisplayed()) {
                    <mulo-page-section
                        [title]="
                            (getFilesAndLinksSectionTitle() | translate) +
                            (files()?.length + links()?.length > 0
                                ? ' (' + (files()?.length + links()?.length) + ')'
                                : '')
                        "
                    >
                        <esp-asset-files-and-links
                            [mmsID]="mmsID"
                            [files]="files()"
                            [links]="links()"
                            [openURLLink]="fullAssetPageRep()?.openURLLink"
                        ></esp-asset-files-and-links>
                    </mulo-page-section>
                }
            }
            @case ('metrics') {
                @if (!isAllMetricsOff()) {
                    <mulo-page-section [title]="sectionLabel">
                        <esp-usage-metrics
                            [mmsID]="mmsID"
                            [doi]="doi"
                            [metrics]="section.metrics"
                            [wosIdentifier]="wosIdentifier"
                        ></esp-usage-metrics>
                    </mulo-page-section>
                }
            }
            @case ('relations') {
                @if (relations.length > 0) {
                    <mulo-page-section [hideOnPrint]="true" [title]="sectionLabel">
                        <esp-related-content
                            [relations]="relations"
                            [resourceType]="fullAssetPageRep()?.resourceTypeValue"
                        ></esp-related-content>
                    </mulo-page-section>
                }
            }
            @case ('details') {
                @if (displayMode() === 'edit') {
                    @if (hasPubDetails$ | async) {
                        @let pubDetailsLabel =
                            'esploro.research.conf.research.portal.label.prefix.publicationDetails' | translate;
                        <mulo-page-section [title]="pubDetailsLabel">
                            <mulo-context-frame
                                (close)="isPubDetailsFormOpen.set(false)"
                                (open)="isPubDetailsFormOpen.set(true)"
                                [isOpen]="isPubDetailsFormOpen()"
                                btnIcon="pencil"
                                [btnLabel]="'research.aria.edit.button' | translate: { value: pubDetailsLabel }"
                                [class.margin-bottom-xs]="!isPubDetailsFormOpen()"
                            >
                                <form
                                    (ngSubmit)="handleSectionSubmit(pubDetailsForm, isPubDetailsFormOpen)"
                                    [formGroup]="pubDetailsForm"
                                    class="padding-right-sm"
                                >
                                    <div>
                                        <mat-form-field
                                            class="is-comfortably-readable"
                                            [muloInputExpander]="pubDetailsForm.get(AssetFieldTypeEnum.Volume).value"
                                        >
                                            <mat-label>{{
                                                'research.status.volume.publication.default' | translate
                                            }}</mat-label>
                                            <input
                                                autocomplete="off"
                                                [formControlName]="AssetFieldTypeEnum.Volume"
                                                matInput
                                                type="text"
                                            />
                                        </mat-form-field>
                                        <mat-form-field
                                            class="is-comfortably-readable"
                                            [muloInputExpander]="pubDetailsForm.get(AssetFieldTypeEnum.Issue).value"
                                        >
                                            <mat-label>{{
                                                'research.status.issue.publication.default' | translate
                                            }}</mat-label>
                                            <input
                                                autocomplete="off"
                                                [formControlName]="AssetFieldTypeEnum.Issue"
                                                matInput
                                                type="text"
                                            />
                                        </mat-form-field>
                                        <mat-form-field
                                            class="is-comfortably-readable"
                                            [muloInputExpander]="pubDetailsForm.get(AssetFieldTypeEnum.Pages).value"
                                        >
                                            <mat-label>{{
                                                'research.status.pages.default.default' | translate
                                            }}</mat-label>
                                            <input
                                                autocomplete="off"
                                                [formControlName]="AssetFieldTypeEnum.Pages"
                                                matInput
                                                type="text"
                                            />
                                            <mat-hint align="start">{{
                                                'research.status.page.hint.publication.default' | translate
                                            }}</mat-hint>
                                        </mat-form-field>
                                    </div>

                                    <div class="save-btn-wrap">
                                        <button
                                            [disabled]="pubDetailsForm.invalid || sectionSubmitInProgress()"
                                            class="is-tight margin-left-sm margin-bottom-sm"
                                            mat-raised-button
                                            type="submit"
                                        >
                                            {{ 'research.portal.entity.item.save' | translate }}
                                        </button>
                                    </div>
                                </form>
                            </mulo-context-frame>
                            @if (!isPubDetailsFormOpen()) {
                                @if (fullAssetPageRep()?.entityModel.publicationDetails; as pubDetails) {
                                    <mulo-dictionary
                                        [alignLabels]="media.is('gt-xs') ? 'right' : 'left'"
                                        [display]="media.is('gt-xs') ? 'rows' : 'column'"
                                    >
                                        @if (assetRevision()?.[AssetFieldTypeEnum.Volume] ?? pubDetails.volume) {
                                            <div
                                                mulo-dictionary-item
                                                [label]="'research.status.volume.publication.default' | translate"
                                                [content]="
                                                    assetRevision()?.[AssetFieldTypeEnum.Volume] ?? pubDetails.volume
                                                "
                                            ></div>
                                        }
                                        @if (assetRevision()?.[AssetFieldTypeEnum.Issue] ?? pubDetails.issue) {
                                            <div
                                                mulo-dictionary-item
                                                [label]="'research.status.issue.publication.default' | translate"
                                                [content]="
                                                    assetRevision()?.[AssetFieldTypeEnum.Issue] ?? pubDetails.issue
                                                "
                                            ></div>
                                        }
                                        @if (
                                            assetRevision()?.[AssetFieldTypeEnum.Pages] !== undefined ||
                                            pubDetails?.spage ||
                                            pubDetails?.epage
                                        ) {
                                            <div
                                                mulo-dictionary-item
                                                [label]="'research.status.pages.default.default' | translate"
                                            >
                                                <ng-container slot="content">
                                                    @if (assetRevision()?.[AssetFieldTypeEnum.Pages] !== undefined) {
                                                        {{ assetRevision()?.[AssetFieldTypeEnum.Pages] }}
                                                    } @else if (pubDetails?.spage && pubDetails?.epage) {
                                                        {{ pubDetails.spage }}-{{ pubDetails.epage }}
                                                    } @else {
                                                        {{ pubDetails?.spage || pubDetails?.epage }}
                                                    }
                                                </ng-container>
                                            </div>
                                        }
                                    </mulo-dictionary>
                                }
                            }
                        </mulo-page-section>
                    }
                    @if (localFieldNames()?.length > 0) {
                        <mulo-page-section
                            [title]="'research.portal.fulldisplay.section.localFields' | translate"
                            class="is-overflow-hidden"
                        >
                            <esp-entity-local-fields
                                [parentId]="mmsID"
                                [items]="filteredLocalFields()"
                                [localFieldNames]="localFieldNames()"
                                [displayMode]="displayMode()"
                                [showRemoveBtn]="false"
                                [uniqueCodes]="true"
                                (itemsChange)="handleLocalFieldsChanged($event)"
                                [requiredFields]="requiredLocalFields()"
                            ></esp-entity-local-fields>
                        </mulo-page-section>
                    }
                }
                @if (fullAssetPageRep()?.details?.length > 0) {
                    <mulo-page-section
                        [title]="'research.portal.fulldisplay.section.details' | translate"
                        class="is-overflow-hidden"
                    >
                        <esp-details [details]="fullAssetPageRep()?.details"></esp-details>
                    </mulo-page-section>
                }
            }
            @case ('incitesHighlights') {
                @if (showIncitesHighlights()) {
                    <mulo-page-section
                        [title]="'esploro.research.conf.research.portal.label.prefix.incitesHighlights' | translate"
                    >
                        <p translate="research.portal.fulldisplay.incitesHighlights.intro"></p>
                        <esp-incites-highlights
                            [data]="incitesHighlights()"
                            [config]="section.incitesHighlights"
                        ></esp-incites-highlights>
                    </mulo-page-section>
                }
            }
            @case ('sdgs') {
                @if (
                    sdgs()?.length > 0 ||
                    (displayMode() === 'edit' && (this.revisionConfig$ | async)?.get(AssetFieldTypeEnum.Sdg))
                ) {
                    <mulo-page-section
                        [title]="'esploro.research.conf.research.portal.label.prefix.sdgs' | translate"
                        [required]="fieldRequiredMap()?.get(AssetFieldTypeEnum.Sdg)"
                        [requiredText]="'research.deposit.required' | translate"
                    >
                        @if (displayMode() === 'edit' && (this.revisionConfig$ | async)?.get(AssetFieldTypeEnum.Sdg)) {
                            <mulo-context-frame
                                (open)="isSdgFormOpen.set(true)"
                                (close)="isSdgFormOpen.set(false)"
                                [isOpen]="isSdgFormOpen()"
                                [class.margin-top-sm]="isSdgFormOpen()"
                                [showOpenBtn]="sdgs()?.length === 0"
                                [btnLabel]="'research.portal.fulldisplay.sdgs.add' | translate"
                            >
                                <form class="padding-top-md" (ngSubmit)="onSaveSdgs()">
                                    <mat-selection-list
                                        [(ngModel)]="sdgRevisions"
                                        [attr.aria-label]="
                                            'esploro.research.conf.research.portal.label.prefix.sdgs' | translate
                                        "
                                        [ngModelOptions]="{ standalone: true }"
                                    >
                                        @for (sdgCodeLabel of allSdgs; track $index) {
                                            <mat-list-option
                                                [value]="sdgCodeLabel.code"
                                                togglePosition="before"
                                                color="primary"
                                            >
                                                {{ sdgCodeLabel.description }}
                                            </mat-list-option>
                                        }
                                    </mat-selection-list>
                                    <div class="save-btn-wrap">
                                        <button
                                            [disabled]="loading() || sectionSubmitInProgress()"
                                            class="is-tight margin-left-sm"
                                            mat-raised-button
                                            type="submit"
                                        >
                                            {{ 'research.portal.entity.item.save' | translate }}
                                        </button>
                                    </div>
                                </form>
                            </mulo-context-frame>
                        }
                        @if (sdgs()?.length > 0 && !isSdgFormOpen()) {
                            <div
                                [class.editable]="
                                    displayMode() === 'edit' &&
                                    (this.revisionConfig$ | async)?.get(AssetFieldTypeEnum.Sdg)
                                "
                            >
                                <p translate="research.portal.fulldisplay.sdgs.intro"></p>

                                <mulo-sdg-icon-list
                                    class="margin-top-sm margin-bottom-sm"
                                    [activeSdgs]="sdgs()"
                                ></mulo-sdg-icon-list>
                                @if (isSdgsFromIncites()) {
                                    <p
                                        translate="research.portal.fulldisplay.sdgs.source"
                                        [translateParams]="{
                                            src: ('research.portal.fulldisplay.sdgs.source.incites' | translate),
                                        }"
                                    ></p>
                                }
                                @if (
                                    displayMode() === 'edit' &&
                                    (this.revisionConfig$ | async)?.get(AssetFieldTypeEnum.Sdg)
                                ) {
                                    <button
                                        (click)="isSdgFormOpen.set(true)"
                                        [attr.aria-label]="'research.portal.fulldisplay.sdgs.edit' | translate"
                                        class="open-form-btn"
                                        mat-button
                                    >
                                        <mat-icon svgIcon="pencil"></mat-icon>
                                    </button>
                                }
                            </div>
                        }
                    </mulo-page-section>
                }
            }
            @case ('relatedMedia') {
                @if (mediaMentions$ | async; as mediaMentions) {
                    @if (mediaMentions.length > 0) {
                        <mulo-page-section
                            [title]="'research.portal.fulldisplay.section.relations.mediaMentions' | translate"
                        >
                            <mat-list class="mulo-elevation-list" role="list">
                                @for (mention of mediaMentions; track $index) {
                                    <mulo-action-list-item
                                        hiddenLabel=""
                                        [hidden]="!mention.visibleInPortal"
                                        [moreBtnLabel]="'research.aria.output.list.menu.more.options' | translate"
                                        class="mulo-elevation-list-item is-wrapping media-mention"
                                        exlDescriptionA11y="related-activity-{{ $index }}"
                                        role="listitem"
                                    >
                                        <mat-icon
                                            class="mat-icon-rtl-mirror"
                                            matListItemIcon
                                            svgIcon="newspaper"
                                        ></mat-icon>

                                        <mulo-brief
                                            [hoverable]="false"
                                            [queryParams]="{ institution: institutionCode }"
                                            [titleClickable]="mention.links?.length > 0"
                                            [titleUrl]="mention.links[0]?.linkURL"
                                            [title]="mention.title"
                                            [type]="mention.type | translate"
                                            class="padding-zero"
                                        >
                                            <ng-container slot="pre-title">
                                                @if (mention.type | translate) {
                                                    <mulo-separator orientation="vertical"></mulo-separator>
                                                }
                                                {{ mention.dateMedia }}
                                            </ng-container>
                                            @if (mention.source) {
                                                <span class="mm-source"
                                                    >{{ mention.source }}
                                                    @if (mention.reporter) {
                                                        ({{ mention.reporter }})
                                                    }
                                                </span>
                                            }
                                        </mulo-brief>
                                    </mulo-action-list-item>
                                }
                            </mat-list>
                        </mulo-page-section>
                    }
                }
            }
        }
    }
</mulo-page-base>
